import { onScopeDispose, ref, shallowRef, watch } from 'vue';
import { ReservationsService } from '@/api/services/reservations.service';
import type { IReservationRangeFilters } from '@/api/services/reservations.service';
import type { IReservation } from '@/interfaces/IReservation';

export interface IReservationAgendaQuery {
  restaurantId: number;
  branchId: number;
  from: string;
  to: string;
  filters: IReservationRangeFilters;
}

/** Refresh the complete visible page window; only a different query resets it. */
export function useReservationAgenda(
  query: () => IReservationAgendaQuery | null,
  onError: (error: unknown) => void
) {
  const rows = shallowRef<IReservation[]>([]);
  const total = ref(0);
  const page = ref(0);
  const loading = ref(false);
  const failed = ref(false);
  let sequence = 0;
  let disposed = false;
  let refreshQueued = false;
  const key = () => JSON.stringify(query());

  async function load(append = false, reset = false): Promise<void> {
    if (disposed) return;
    if (!reset && loading.value) {
      if (!append) refreshQueued = true;
      return;
    }
    const request = ++sequence;
    const input = query();
    const requestKey = key();
    const current = () =>
      !disposed && request === sequence && requestKey === key();
    if (reset) {
      rows.value = [];
      total.value = 0;
      page.value = 0;
      refreshQueued = false;
    }
    failed.value = false;
    if (!input) {
      loading.value = false;
      return;
    }
    loading.value = true;
    let lastPage = append ? page.value + 1 : Math.max(1, page.value);
    const loaded = new Map<number, IReservation>(
      append ? rows.value.map((row) => [row.id, row]) : []
    );
    let nextTotal = total.value;
    let loadedPage = 0;
    try {
      for (let index = append ? lastPage : 1; index <= lastPage; index++) {
        const result = await ReservationsService.getRange(
          input.restaurantId,
          input.branchId,
          input.from,
          input.to,
          input.filters,
          index
        );
        if (!current()) return;
        nextTotal = result.total;
        loadedPage = result.page;
        for (const row of result.items) loaded.set(row.id, row);
        if (!append)
          lastPage = Math.min(lastPage, Math.max(1, Math.ceil(nextTotal / 50)));
        else if (loadedPage > Math.max(1, Math.ceil(nextTotal / 50)))
          refreshQueued = true;
      }
      if (!current()) return;
      rows.value = [...loaded.values()];
      total.value = nextTotal;
      page.value = loadedPage;
    } catch (error) {
      if (current()) {
        failed.value = true;
        onError(error);
      }
    } finally {
      if (current()) {
        loading.value = false;
        if (refreshQueued) {
          refreshQueued = false;
          void load();
        }
      }
    }
  }

  watch(key, () => void load(false, true), { immediate: true });
  onScopeDispose(() => {
    disposed = true;
    sequence++;
  });
  return { rows, total, page, loading, failed, load };
}
